import { useQueryClient, useQuery, useMutation } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import { ExplainerVideoButton } from "@/components/explainer-video-button";
import { GuideButton } from "@/components/guide-button";
import { GuideNudgeBanner } from "@/components/guide-nudge-banner";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import AppLayout from "@/components/app-layout";
import { type MarketResearch, type MarketResearchHistory, hasFeatureAccess, type AccountType } from "@shared/schema";
import type { SetupField } from "@shared/buy-box-setup";
import { TrendingUp, Building2, Search, DollarSign, Users, Clock, ExternalLink, Flame, ArrowLeft, Info, HelpCircle, Sparkles, RefreshCw, Activity, Target, Loader2, Lock } from "lucide-react";
import { useState, useMemo, useEffect, useRef } from "react";
import { useParams, Link, useLocation } from "wouter";
import { ToastAction } from "@/components/ui/toast";
import { useToast } from "@/hooks/use-toast";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import {
  MONTH_AXIS_EDGE_PADDING_PX,
  bucketTrendsByMonth,
  canonicalHistoryMonths,
  filterChartMonthRange,
  monthChartMinWidth,
  monthAxisDomain,
  monthLabelToOffset,
  offsetToMonthLabel,
  type ResearchChartRange,
} from "@/lib/research-chart-months";

type ChartMetric = "searches" | "occupancy" | "activeUnits" | "upcomingUnits" | "priceShared" | "pricePrivate" | "ratio" | "daysToFirst" | "daysTo80";

const chartMetricLabels: Record<ChartMetric, string> = {
  searches: "Monthly Searches",
  occupancy: "Occupancy %",
  activeUnits: "Active Units",
  upcomingUnits: "Upcoming Units",
  priceShared: "Avg Weekly Shared ($)",
  pricePrivate: "Avg Weekly Private ($)",
  ratio: "Search:Room Ratio",
  daysToFirst: "Days to 1st Booking",
  daysTo80: "Days to 80% Occupancy",
};

const chartMetricDescriptions: Record<ChartMetric, string> = {
  searches: "Total monthly search volume on PadSplit for rooms in this market. Higher searches indicate stronger renter demand.",
  occupancy: "Percentage of listed rooms currently occupied. Higher occupancy means less vacancy risk for hosts.",
  activeUnits: "Total number of rooms currently live and listed on PadSplit in this market.",
  upcomingUnits: "Rooms approved or in the pipeline but not yet live. High numbers signal growing host interest and market momentum.",
  priceShared: "Average weekly rent for a shared room (multiple beds in one room) on PadSplit in this market.",
  pricePrivate: "Average weekly rent for a private room (single occupant) on PadSplit in this market.",
  ratio: "Monthly searches divided by available (unoccupied) rooms. A higher ratio means more renters are competing for fewer rooms — strong demand signal.",
  daysToFirst: "Average number of days from listing a room to receiving the first booking. Lower is better.",
  daysTo80: "Average number of days from listing to reaching 80% occupancy across a property's rooms. Lower means faster fill-up.",
};

const CHART_COLORS = [
  "#2563eb", "#dc2626", "#16a34a", "#9333ea", "#ea580c",
  "#0891b2", "#c026d3", "#65a30d", "#0369a1", "#be123c",
  "#d97706", "#4f46e5", "#059669", "#e11d48", "#7c3aed",
  "#0284c7", "#db2777", "#84cc16", "#06b6d4", "#f59e0b",
  "#6366f1", "#10b981", "#f43f5e", "#8b5cf6", "#14b8a6",
  "#78716c", "#0e7490", "#a21caf", "#b91c1c", "#15803d",
  "#7e22ce", "#c2410c", "#0f766e", "#6d28d9", "#b45309",
  "#1d4ed8", "#9f1239", "#4338ca", "#047857", "#e879f9",
  "#f97316", "#06b6d4", "#ec4899", "#22c55e", "#a855f7",
  "#ef4444", "#3b82f6", "#eab308", "#14b8a6", "#f43f5e",
];

function MonthYearTick({ x, y, payload }: any) {
  const label = offsetToMonthLabel(Number(payload?.value));
  if (!label) return null;
  const [month, year] = label.split(" ");
  const shortMonth = month.length > 5 ? month.substring(0, 3) : month;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={14} textAnchor="middle" fill="#6b7280" fontSize={11}>
        {shortMonth}
      </text>
      <text x={0} y={0} dy={28} textAnchor="middle" fill="#9ca3af" fontSize={10}>
        {year}
      </text>
    </g>
  );
}

function CenteredTooltip({ active, payload, label }: any) {
  if (!active || !payload || payload.length === 0) return null;

  const monthLabel = offsetToMonthLabel(Number(label)) || label;

  const sorted = [...payload].filter((e: any) => e.value != null).sort((a: any, b: any) => Number(b.value) - Number(a.value));

  return (
    <div
      style={{
        pointerEvents: "none",
        zIndex: 50,
        maxWidth: "min(280px, 85vw)",
      }}
    >
      <div
        className="bg-white border border-gray-200 rounded-lg shadow-lg px-3 py-2"
      >
        <p className="text-xs font-semibold text-gray-700 mb-1.5 border-b border-gray-100 pb-1">{monthLabel}</p>
        <div className="space-y-0.5">
          {sorted.map((entry: any, i: number) => (
            <div key={i} className="flex items-center gap-2 text-[11px]">
              <span
                className="w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-gray-600 truncate max-w-[120px]">{entry.name}</span>
              <span className="font-semibold text-gray-900 ml-auto">{Number(entry.value).toLocaleString()}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

interface CityDataPoint {
  month: string;
  monthOffset: number;
  searches: number;
  occupancy: number;
  activeUnits: number;
  upcomingUnits: number;
  priceShared: number;
  pricePrivate: number;
  ratio: number;
  daysToFirst: number;
  daysTo80: number;
}

function buildHistoryForCity(city: string, state: string, history: MarketResearchHistory[], currentMarket: MarketResearch): CityDataPoint[] {
  const cityHistory = history.filter((h) => h.city === city && h.state === state);

  const points: Record<string, Omit<CityDataPoint, "month" | "monthOffset">> = {};

  for (const h of canonicalHistoryMonths(cityHistory)) {
    const searches = h.searches || 0;
    const activeUnits = h.activeUnits || 0;
    const occupancy = h.occupancy || 0;
    const priceShared = Number(h.avgPriceShared || 0);
    const pricePrivate = Number(h.avgPricePrivate || 0);
    const upcomingUnits = h.upcomingUnits || 0;
    const daysToFirst = h.daysToFirstBooking || 0;
    const daysTo80 = h.daysTo80Occupancy || 0;
    const availableEstimate = activeUnits > 0 ? Math.round(activeUnits * (1 - occupancy / 100)) : 0;
    const ratio = availableEstimate > 0 ? parseFloat((searches / availableEstimate).toFixed(1)) : 0;
    points[h.month] = { searches, occupancy, activeUnits, upcomingUnits, priceShared, pricePrivate, ratio, daysToFirst, daysTo80 };
  }

  const currentMonth = currentMarket.dataMonth;
  if (currentMonth && monthLabelToOffset(currentMonth) !== undefined) {
    // Only inject the current snapshot if there isn't already a
    // history row for that month — history is the source of truth
    // for any month it covers so we don't overwrite a row that
    // might have richer/different values.
    const canonicalMonth = offsetToMonthLabel(monthLabelToOffset(currentMonth)!);
    if (canonicalMonth && !points[canonicalMonth]) {
      points[canonicalMonth] = {
        searches: Number(currentMarket.totalSearchVolume || 0),
        occupancy: Number(currentMarket.occupancyRate || 0),
        activeUnits: Number(currentMarket.totalUnits || 0),
        upcomingUnits: Number(currentMarket.upcomingUnits || 0),
        priceShared: Number(currentMarket.avgPriceShared || 0),
        pricePrivate: Number(currentMarket.avgPricePrivate || 0),
        ratio: Number(currentMarket.searchToRoomRatio || 0),
        daysToFirst: Number(currentMarket.daysToFirstBooking || 0),
        daysTo80: Number(currentMarket.daysTo80Occupancy || 0),
      };
    }
  }

  return Object.entries(points)
    .map(([month, p]) => ({ month, monthOffset: monthLabelToOffset(month), ...p }))
    .filter((p): p is CityDataPoint => p.monthOffset !== undefined)
    .sort((a, b) => a.monthOffset - b.monthOffset);
}

function buildComparisonData(
  markets: MarketResearch[],
  history: MarketResearchHistory[],
  selectedCities: string[],
  metric: ChartMetric
) {
  const cityDataMap = new Map<string, CityDataPoint[]>();
  for (const cityName of selectedCities) {
    const m = markets.find((mk) => mk.city === cityName);
    if (m) {
      cityDataMap.set(cityName, buildHistoryForCity(m.city, m.state, history, m));
    }
  }

  const allMonths = new Set<string>();
  for (const data of cityDataMap.values()) {
    for (const pt of data) allMonths.add(pt.month);
  }

  const sortedMonths = Array.from(allMonths)
    .map((m) => ({ m, off: monthLabelToOffset(m) }))
    .filter((x): x is { m: string; off: number } => x.off !== undefined)
    .sort((a, b) => a.off - b.off)
    .map((x) => x.m);

  return sortedMonths.map((month) => {
    const point: Record<string, string | number> & { month: string; monthOffset: number } = {
      month, monthOffset: monthLabelToOffset(month) ?? 0,
    };
    for (const cityName of selectedCities) {
      const data = cityDataMap.get(cityName);
      if (data) {
        const pt = data.find((d) => d.month === month);
        if (pt) point[cityName] = pt[metric];
      }
    }
    return point;
  });
}

// Task #1685 — NDJSON stream reader for the AI market analysis. The
// server emits one JSON object per line:
//   { "type": "chunk", "text": "..." }
//   { "type": "done",  "summary": "...", "model": "...", "generatedAt": "..." }
//   { "type": "error", "message": "..." }
// We parse incrementally so the UI can show tokens as they arrive
// rather than waiting for the full ~20–30s response.
type AiStreamEvent =
  | { type: "chunk"; text: string }
  | { type: "done"; summary: string; model?: string; generatedAt: string; cached?: boolean }
  | { type: "error"; message: string };

async function consumeAiStream(
  res: Response,
  onEvent: (event: AiStreamEvent) => void,
): Promise<void> {
  if (!res.body) throw new Error("No response body to stream");
  const reader = res.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let nl = buffer.indexOf("\n");
    while (nl !== -1) {
      const line = buffer.slice(0, nl).trim();
      buffer = buffer.slice(nl + 1);
      if (line.length > 0) {
        try {
          onEvent(JSON.parse(line) as AiStreamEvent);
        } catch {
          // Ignore malformed lines — Gemini occasionally yields empty
          // padding the server might forward; the next chunk should
          // restore parsing.
        }
      }
      nl = buffer.indexOf("\n");
    }
  }
  const tail = buffer.trim();
  if (tail.length > 0) {
    try {
      onEvent(JSON.parse(tail) as AiStreamEvent);
    } catch {}
  }
}

// Task #1680 — Step-by-step progress indicator (kept as a fallback for
// the brief "connecting" window before the first streamed chunk arrives,
// since Gemini can take a few seconds to start emitting text).
// Task #1685 — Once the first chunk lands the UI swaps to showing the
// streaming summary directly, so this component is mostly off-screen.
const MARKET_ANALYSIS_STEPS = [
  { label: "Analyzing market data", ms: 4500 },
  { label: "Researching regulations", ms: 6000 },
  { label: "Comparing neighboring metros", ms: 6000 },
  { label: "Writing summary", ms: Number.POSITIVE_INFINITY },
];

function MarketAnalysisProgress({ label, testId }: { label: string; testId?: string }) {
  const [activeStep, setActiveStep] = useState(0);

  useEffect(() => {
    setActiveStep(0);
    const timers: ReturnType<typeof setTimeout>[] = [];
    let elapsed = 0;
    for (let i = 0; i < MARKET_ANALYSIS_STEPS.length - 1; i++) {
      elapsed += MARKET_ANALYSIS_STEPS[i].ms;
      const next = i + 1;
      timers.push(setTimeout(() => setActiveStep(next), elapsed));
    }
    return () => {
      for (const t of timers) clearTimeout(t);
    };
  }, []);

  return (
    <div className="flex flex-col gap-3" data-testid={testId}>
      <div className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300">
        <Loader2 className="h-4 w-4 animate-spin shrink-0" />
        <span className="text-xs font-medium">{label}</span>
      </div>
      <ol className="space-y-1.5 pl-1">
        {MARKET_ANALYSIS_STEPS.map((step, i) => {
          const isDone = i < activeStep;
          const isActive = i === activeStep;
          return (
            <li
              key={step.label}
              className="flex items-center gap-2 text-xs"
              data-testid={`step-ai-progress-${i}`}
              data-state={isDone ? "done" : isActive ? "active" : "pending"}
            >
              <span className="w-4 h-4 flex items-center justify-center shrink-0">
                {isDone ? (
                  <span className="w-3.5 h-3.5 rounded-full bg-indigo-600 dark:bg-indigo-500 text-white text-[10px] leading-none flex items-center justify-center">
                    ✓
                  </span>
                ) : isActive ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin text-indigo-600 dark:text-indigo-400" />
                ) : (
                  <span className="w-2 h-2 rounded-full bg-indigo-200 dark:bg-indigo-900" />
                )}
              </span>
              <span
                className={
                  isDone
                    ? "text-gray-600 dark:text-muted-foreground line-through decoration-indigo-300"
                    : isActive
                      ? "text-gray-900 dark:text-foreground font-medium"
                      : "text-gray-400 dark:text-muted-foreground"
                }
              >
                {step.label}
                {isActive ? "…" : ""}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default function AppResearchCity() {
  const queryClient = useQueryClient();
  const params = useParams<{ slug: string }>();
  const slug = params.slug || "";

  const { data: markets = [], isLoading } = useQuery<MarketResearch[]>({
    queryKey: ["/api/app/market-research"],
  });

  const { data: history = [] } = useQuery<MarketResearchHistory[]>({
    queryKey: ["/api/app/market-research/history"],
  });

  const [chartMetric, setChartMetric] = useState<ChartMetric>("searches");
  const [chartRange, setChartRange] = useState<ResearchChartRange>("all");
  const [compareCities, setCompareCities] = useState<string[]>([]);
  const [compareMinRooms, setCompareMinRooms] = useState<number>(1);
  const COMPARE_MIN_ROOMS_PRESETS = [1, 50, 100, 250, 500, 1000];
  const [showComparison, setShowComparison] = useState(false);
  const [showTrendsOverlay, setShowTrendsOverlay] = useState(false);
  // Task #1139 — AI Market Analysis state.
  //   • `customSummary`      — Growth/Pro user's most recent live
  //     custom-prompt result. Lower tiers never set this.
  //   • `extraInstructions`  — the textarea content for the custom run.
  // Task #1676 — `analysisRequested` is gone; the cached read now fires
  // automatically on page load and generation triggers on a cache miss.
  const [customSummary, setCustomSummary] = useState<{ summary: string; generatedAt: string } | null>(null);
  const [extraInstructions, setExtraInstructions] = useState("");
  // Task #1685 — Live streamed text from Gemini. While a stream is in
  // flight we append chunks here and render them in place of the timed
  // step indicator. `streamingFor` tracks which path is producing the
  // stream so the right slot ("custom" vs "auto") renders the partial
  // output; null when nothing is streaming.
  const [streamingText, setStreamingText] = useState("");
  const [streamingFor, setStreamingFor] = useState<null | "auto" | "custom">(null);
  const [scanSetupErrors, setScanSetupErrors] = useState<{
    buyBox: boolean;
    underwriting: boolean;
    missingBuyBox: SetupField[];
    missingUnderwriting: SetupField[];
  } | null>(null);
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const meQuery = useQuery<{ user: { id: number; selectedPlan?: string; accountKind?: string | null }; effectivePlan?: string }>({
    queryKey: ["/api/app/auth/me"],
  });
  const effectivePlan = (meQuery.data?.effectivePlan || meQuery.data?.user?.selectedPlan || "Host") as AccountType;
  const canCustomizeAnalysis =
    meQuery.data?.user?.accountKind != null || hasFeatureAccess(effectivePlan, "Growth");

  const scanMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      if (!market) throw new Error("No market selected");
      // Task #1997 — default to the city's SHARED room rate, falling back
      // to the private rate (and then the backend's 200 floor) when no
      // shared data exists. The backend field name is preserved to keep
      // this change minimal; it represents the weekly per-room rate.
      const sharedRate = market.avgPriceShared != null && market.avgPriceShared !== ""
        ? market.avgPriceShared
        : market.avgPricePrivate;
      const res = await apiRequest("POST", "/api/app/buy-box/scan", {
        city: market.city,
        state: market.state,
        avgPricePrivate: sharedRate,
      });
      return res.json();
    },
    onSuccess: (data) => {
      setScanSetupErrors(null);
      queryClient.invalidateQueries({ queryKey: ["/api/app/buy-box/reports"] });
      const reportPath = data.reportId
        ? `/app/buy-box/reports/${data.reportId}`
        : `/app/buy-box/reports`;
      const isCached = Boolean(data.cached);
      toast({
        title: isCached ? "Reusing recent scan results" : "Buy Box scan started",
        description: (
          <Link
            href={reportPath}
            className="cursor-pointer block"
          >
            {isCached
              ? `Reusing recent ${market?.city}, ${market?.state} scan results — should be ready in seconds.`
              : `Scanning Zillow for properties in ${market?.city}, ${market?.state}.`}
          </Link>
        ),
        action: (
          <ToastAction
            altText="View report"
            onClick={(e) => { e.stopPropagation(); navigate(reportPath); }}
            data-testid="button-view-buy-box-report"
          >
            View Report
          </ToastAction>
        ),
      });
    },
    onError: (e: Error) => {
      // Detect the structured setup-incompleteness 400 from the server.
      // Error message format: "400: {\"message\":\"Setup incomplete\",\"incomplete\":[...]}"
      try {
        const jsonStart = e.message.indexOf("{");
        if (e.message.startsWith("400:") && jsonStart !== -1) {
          const parsed = JSON.parse(e.message.slice(jsonStart)) as {
            message?: string;
            incomplete?: string[];
            missingFields?: { buyBox?: SetupField[]; underwriting?: SetupField[] };
          };
          if (parsed.incomplete && Array.isArray(parsed.incomplete)) {
            setScanSetupErrors({
              buyBox: parsed.incomplete.includes("buyBox"),
              underwriting: parsed.incomplete.includes("underwriting"),
              missingBuyBox: parsed.missingFields?.buyBox ?? [],
              missingUnderwriting: parsed.missingFields?.underwriting ?? [],
            });
            return;
          }
        }
      } catch {
        // fall through to generic toast
      }
      setScanSetupErrors(null);
      toast({ title: "Scan failed", description: parseErrorMessage(e, "Couldn't start the Buy Box scan. Please try again."), variant: "destructive" });
    },
  });

  const market = useMemo(() => {
    return markets.find((m) => {
      const s = `${m.city}-${m.state}`.toLowerCase().replace(/\s/g, "-");
      return s === slug;
    });
  }, [markets, slug]);
  usePageTitle(market ? `${market.city}, ${market.state}` : "Market Research");

  const historyData = useMemo(() => {
    if (!market) return [];
    return buildHistoryForCity(market.city, market.state, history, market);
  }, [market, history]);

  // Limit chart presentation only; retain the full history for AI analysis.
  const chartHistoryData = useMemo(
    () => filterChartMonthRange(historyData, chartRange),
    [historyData, chartRange],
  );

  const { data: trendsData, isLoading: trendsLoading, isError: trendsError } = useQuery<{
    geo: string;
    keyword: string;
    data: Array<{ date: string; value: number }>;
    stale?: boolean;
    fetchedAt?: string;
  }>({
    queryKey: ["/api/app/market-research/google-trends", market?.city, market?.state],
    queryFn: async () => {
      const params = new URLSearchParams({ city: market!.city, state: market!.state });
      const res = await fetch(`/api/app/market-research/google-trends?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error(`${res.status}: ${await res.text()}`);
      return res.json();
    },
    enabled: showTrendsOverlay && !showComparison && !!market,
  });

  const hasHistoricalData = useMemo(() => {
    if (!market) return false;
    return history.some((h) => h.city === market.city && h.state === market.state);
  }, [market, history]);

  const allCities = useMemo(() => {
    return markets
      .filter((m) => m.level === "city")
      .sort((a, b) => a.city.localeCompare(b.city));
  }, [markets]);

  // Filter the comparison-picker chip grid by minimum active units, but
  // always include the current city so the "1/N selected" counter and
  // the always-on chip stay correct.
  const comparePickerCities = useMemo(() => {
    return allCities.filter(
      (m) => m.city === market?.city || Number(m.totalUnits || 0) >= compareMinRooms,
    );
  }, [allCities, market, compareMinRooms]);

  const comparisonData = useMemo(() => {
    if (!market) return [];
    const cities = [market.city, ...compareCities.filter((c) => c !== market.city)];
    return filterChartMonthRange(
      buildComparisonData(markets, history, cities, chartMetric),
      chartRange,
    );
  }, [markets, history, market, compareCities, chartMetric, chartRange]);

  const activeCitiesInChart = useMemo(() => {
    if (!market) return [];
    const cities = [market.city, ...compareCities.filter((c) => c !== market.city)];
    if (comparisonData.length === 0) return cities;
    const lastPoint = comparisonData[comparisonData.length - 1];
    return [...cities].sort((a, b) => {
      const va = Number(lastPoint[a] ?? 0);
      const vb = Number(lastPoint[b] ?? 0);
      return vb - va;
    });
  }, [market, compareCities, comparisonData]);

  const xAxisTicks = useMemo(() => {
    const offsets = new Set<number>();
    const data = showComparison ? comparisonData : chartHistoryData;
    for (const pt of data) {
      if (pt.monthOffset !== undefined) offsets.add(pt.monthOffset as number);
    }
    return Array.from(offsets).sort((a, b) => a - b);
  }, [showComparison, comparisonData, chartHistoryData]);

  const xAxisDomain = useMemo(() => monthAxisDomain(xAxisTicks), [xAxisTicks]);

  const comparisonChartHeight = useMemo(() => {
    const count = activeCitiesInChart.length;
    return Math.max(400, count * 18 + 100);
  }, [activeCitiesInChart]);

  const singleCityChartData = useMemo(() => {
    if (!showTrendsOverlay || !trendsData?.data?.length) return chartHistoryData;

    const trendsMap = bucketTrendsByMonth(
      trendsData.data,
      chartHistoryData.map((d) => d.monthOffset),
    );

    const offsetSet = new Set<number>(chartHistoryData.map((d) => d.monthOffset));
    for (const offset of trendsMap.keys()) offsetSet.add(offset);

    const sortedOffsets = Array.from(offsetSet).sort((a, b) => a - b);

    const padSplitMap = new Map<number, CityDataPoint>();
    for (const d of chartHistoryData) padSplitMap.set(d.monthOffset, d);

    return sortedOffsets.map((offset) => {
      const base = padSplitMap.get(offset);
      return {
        month: base?.month || "",
        monthOffset: offset,
        searches: base?.searches,
        occupancy: base?.occupancy,
        activeUnits: base?.activeUnits,
        upcomingUnits: base?.upcomingUnits,
        priceShared: base?.priceShared,
        pricePrivate: base?.pricePrivate,
        ratio: base?.ratio,
        daysToFirst: base?.daysToFirst,
        daysTo80: base?.daysTo80,
        googleTrends: trendsMap.get(offset),
      } as CityDataPoint & { googleTrends?: number };
    });
  }, [chartHistoryData, trendsData, showTrendsOverlay]);

  // Task #1139 — Cached read of the pre-computed monthly analysis.
  // Task #1676 — Auto-fires on page load (no button click required).
  // Returns `null` when the cached row is missing (404) — the
  // useEffect below watches for that and auto-triggers on-demand
  // generation so users never see a "no analysis yet" dead end.
  type CachedSummary = { summary: string; model: string; generatedAt: string; cached: boolean };
  const cachedSummaryQuery = useQuery<CachedSummary | null>({
    queryKey: ["/api/app/market-research/ai-summary", market?.city, market?.state],
    queryFn: async () => {
      const params = new URLSearchParams({ city: market!.city, state: market!.state });
      const res = await fetch(`/api/app/market-research/ai-summary?${params}`, { credentials: "include" });
      if (res.status !== 404 && !res.ok) {
        throw new Error(`${res.status}: ${await res.text()}`);
      }
      const data: CachedSummary | null = res.status === 404 ? null : await res.json();
      return data;
    },
    enabled: !!market,
  });

  // Task #1676 — On-demand generation mutation. Fires automatically when
  // the cached read returns null (no row yet). Generates and stores the
  // analysis so the monthly cron picks it up going forward.
  //
  // onSuccess uses `variables` (the mutation payload) NOT `market` from
  // closure to avoid a cross-city race: if the user navigates to City B
  // while City A's Gemini call is still in-flight, the response lands
  // under City A's cache key and is ignored for the City B view.
  // Task #1685 — Streams the on-demand cache-miss generation via NDJSON
  // so the analysis renders token-by-token. We still expose the result
  // shape react-query expects (CachedSummary) on success so the rest of
  // this page can keep reading from `generateMutation.data` unchanged.
  const generateMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (payload: { city: string; state: string }) => {
      setStreamingText("");
      setStreamingFor("auto");
      let accumulated = "";
      const captured: { final: CachedSummary | null; error: string | null } = { final: null, error: null };
      const res = await fetch("/api/app/market-research/ai-summary/generate/stream", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        throw new Error(`${res.status}: ${await res.text()}`);
      }
      await consumeAiStream(res, (event) => {
        if (event.type === "chunk") {
          accumulated += event.text;
          setStreamingText(accumulated);
        } else if (event.type === "done") {
          captured.final = {
            summary: event.summary,
            model: event.model ?? "",
            generatedAt: event.generatedAt,
            cached: event.cached ?? false,
          };
        } else if (event.type === "error") {
          captured.error = event.message;
        }
      });
      if (captured.error) throw new Error(captured.error);
      if (!captured.final) throw new Error("Stream ended without a final summary");
      return captured.final;
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        ["/api/app/market-research/ai-summary", variables.city, variables.state],
        data,
      );
    },
    onSettled: () => {
      setStreamingFor(null);
      setStreamingText("");
    },
  });

  // Ref-based guard: tracks which "city|state" key generation has already
  // been started for. Prevents re-entrancy without needing mutation flags
  // in the effect dependency array (which could cause stale closure bugs).
  // Reset to null on city change so the next uncached city re-triggers.
  const generationStartedForRef = useRef<string | null>(null);

  // Task #1676 — Auto-trigger generation when the cached query settles
  // with no row (404 → null). The ref prevents duplicate fires for the
  // same city; city navigation resets the ref via the effect below.
  useEffect(() => {
    if (!market) return;
    if (!cachedSummaryQuery.isSuccess || cachedSummaryQuery.data !== null) return;
    const cityKey = `${market.city}|${market.state}`;
    if (generationStartedForRef.current === cityKey) return;
    generationStartedForRef.current = cityKey;
    generateMutation.mutate({ city: market.city, state: market.state });
  }, [market?.city, market?.state, cachedSummaryQuery.isSuccess, cachedSummaryQuery.data]);

  // Task #1139 — Reset per-city custom-prompt result when the user
  // navigates between cities so the previous city's analysis doesn't
  // bleed across. Reset generate mutation and the ref so generation
  // re-fires if the new city also has no cached analysis.
  useEffect(() => {
    setCustomSummary(null);
    setExtraInstructions("");
    generationStartedForRef.current = null;
    generateMutation.reset();
  }, [market?.city, market?.state]);

  // Task #1139 — Live custom-prompt run. Growth/Pro only — the server
  // tier-gates this with 403, but we also hide the UI for lower tiers
  // so they don't see a button that always errors.
  // Task #1685 — Streams the Growth/Pro custom prompt run via NDJSON.
  // While in flight `streamingText` is rendered above the textarea so
  // the user sees the analysis arrive token-by-token.
  const customPromptMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async (payload: { extraInstructions: string }) => {
      if (!market || !historyData || historyData.length === 0) {
        throw new Error("No market data available");
      }
      const lastPoint = historyData[historyData.length - 1];
      setStreamingText("");
      setStreamingFor("custom");
      let accumulated = "";
      const captured: { final: { summary: string; generatedAt: string } | null; error: string | null } = { final: null, error: null };
      const res = await fetch("/api/app/market-research/ai-summary/stream", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          city: market.city,
          state: market.state,
          currentData: lastPoint,
          historyData,
          extraInstructions: payload.extraInstructions,
        }),
      });
      if (!res.ok) {
        throw new Error(`${res.status}: ${await res.text()}`);
      }
      await consumeAiStream(res, (event) => {
        if (event.type === "chunk") {
          accumulated += event.text;
          setStreamingText(accumulated);
        } else if (event.type === "done") {
          captured.final = { summary: event.summary, generatedAt: event.generatedAt };
        } else if (event.type === "error") {
          captured.error = event.message;
        }
      });
      if (captured.error) throw new Error(captured.error);
      if (!captured.final) throw new Error("Stream ended without a final summary");
      return captured.final;
    },
    onSuccess: (data) => {
      setCustomSummary({ summary: data.summary, generatedAt: data.generatedAt });
    },
    onError: (e: Error) => {
      toast({
        title: "Custom analysis failed",
        description: parseErrorMessage(e, "Couldn't generate the custom analysis. Please try again."),
        variant: "destructive",
      });
    },
    onSettled: () => {
      setStreamingFor(null);
      setStreamingText("");
    },
  });

  const submitCustomPrompt = () => {
    const trimmed = extraInstructions.trim();
    if (!trimmed) {
      toast({ title: "Add instructions", description: "Please describe what you'd like the analysis to focus on.", variant: "destructive" });
      return;
    }
    customPromptMutation.mutate({ extraInstructions: trimmed });
  };

  // Task #1676 — Scope generate mutation results to the current city so a
  // stale in-flight response from a previous city can't bleed into the
  // current view. Use mutation `variables` (the request payload) as the
  // source of truth rather than the `market` closure.
  const generateIsForCurrentCity =
    generateMutation.variables?.city === market?.city &&
    generateMutation.variables?.state === market?.state;
  const generateDataForCurrentCity = generateIsForCurrentCity ? generateMutation.data : undefined;

  const aiSummary = customSummary?.summary ?? generateDataForCurrentCity?.summary ?? cachedSummaryQuery.data?.summary ?? null;
  const aiSummaryGeneratedAt = customSummary?.generatedAt ?? generateDataForCurrentCity?.generatedAt ?? cachedSummaryQuery.data?.generatedAt ?? null;
  const aiSummaryIsCustom = customSummary !== null;
  const aiSummaryGenerating =
    customPromptMutation.isPending || (generateIsForCurrentCity && generateMutation.isPending);
  // Active generation always wins over the cached-read skeleton so the
  // step-by-step progress UI is never hidden by a still-loading cache query.
  const aiSummaryLoading = cachedSummaryQuery.isLoading && !aiSummaryGenerating;
  const aiSummaryGeneratingLabel = customPromptMutation.isPending
    ? "Generating your custom analysis…"
    : "Generating analysis for this city…";
  // Task #1685 — Once the stream starts emitting we show the live text
  // instead of the timed step indicator. We still fall back to the step
  // indicator for the brief "connecting" window before the first chunk
  // lands (Gemini sometimes takes a few seconds to begin).
  const isStreaming = aiSummaryGenerating && streamingFor !== null && streamingText.length > 0;

  const toggleCompareCity = (city: string) => {
    if (market && city === market.city) return;
    setCompareCities((prev) => {
      if (prev.includes(city)) return prev.filter((c) => c !== city);
      if (prev.length >= 49) return prev;
      return [...prev, city];
    });
  };

  const selectAllCompare = () => {
    if (!market) return;
    setCompareCities((prev) => {
      const candidates = comparePickerCities
        .map((m) => m.city)
        .filter((c) => c !== market.city && !prev.includes(c));
      const remaining = 49 - prev.length;
      if (remaining <= 0) return prev;
      return [...prev, ...candidates.slice(0, remaining)];
    });
  };

  const clearAllCompare = () => {
    setCompareCities([]);
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-48" />
            <div className="h-64 bg-gray-200 rounded" />
          </div>
        </div>
      </AppLayout>
    );
  }

  if (!market) {
    return (
      <AppLayout>
        <div className="p-6">
          <Link href="/app/research">
            <span className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1 cursor-pointer mb-4">
              <ArrowLeft className="h-4 w-4" /> Back to Research
            </span>
          </Link>
          <p className="text-gray-500">City not found.</p>
        </div>
      </AppLayout>
    );
  }

  const growthRate = Number(market.totalUnits || 0) > 0
    ? ((Number(market.upcomingUnits || 0) / Number(market.totalUnits || 0)) * 100).toFixed(1)
    : "0";

  return (
    <AppLayout>
      <div className="p-6 max-w-6xl">
        <Link href="/app/research">
          <span className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1 cursor-pointer mb-4" data-testid="link-back-research">
            <ArrowLeft className="h-4 w-4" /> Back to Research
          </span>
        </Link>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2" data-testid="text-city-title" data-tour="research-city-heading">
              <Building2 className="h-6 w-6 text-blue-600" />
              {market.city}, {market.state}
            </h1>
            {market.dataMonth && (
              <p className="text-sm text-gray-500 mt-1">Data: {market.dataMonth}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {market.sourceUrl && (
              <a
                href={market.sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium"
                data-testid="link-view-padsplit"
              >
                View on PadSplit
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
            <div className="flex items-center gap-2">
              <ExplainerVideoButton pageKey="research-city" />
              <GuideButton pageKey="research-city" />
            </div>
          </div>
          <GuideNudgeBanner pageKey="research-city" />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-xs text-blue-600 font-medium mb-1">
              <TrendingUp className="h-3.5 w-3.5" />
              Search:Room Ratio
            </div>
            <div className="text-xl font-bold text-blue-800" data-testid="text-detail-ratio">
              {Math.round(Number(market.searchToRoomRatio || 0))}:1
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-xs text-green-600 font-medium mb-1">
              <Users className="h-3.5 w-3.5" />
              Occupancy
            </div>
            <div className="text-xl font-bold text-green-800" data-testid="text-detail-occupancy">
              {market.occupancyRate}%
            </div>
          </div>
          <div className="bg-purple-50 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-xs text-purple-600 font-medium mb-1">
              <Search className="h-3.5 w-3.5" />
              Monthly Searches
            </div>
            <div className="text-xl font-bold text-purple-800" data-testid="text-detail-searches">
              {Number(market.totalSearchVolume || 0).toLocaleString()}
            </div>
          </div>
          <div className="bg-orange-50 rounded-lg p-3">
            <div className="flex items-center gap-1.5 text-xs text-orange-600 font-medium mb-1">
              <Flame className="h-3.5 w-3.5" />
              Growth Rate
            </div>
            <div className="text-xl font-bold text-orange-800" data-testid="text-detail-growth">
              {growthRate}%
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="border border-gray-100 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">Active Units</div>
            <div className="font-semibold text-gray-900" data-testid="text-detail-active-units">
              {Number(market.totalUnits || 0).toLocaleString()}
            </div>
          </div>
          <div className="border border-gray-100 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">Upcoming Units</div>
            <div className="font-semibold text-gray-900" data-testid="text-detail-upcoming-units">
              {(market.upcomingUnits ?? 0).toLocaleString()}
            </div>
          </div>
          <div className="border border-gray-100 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">Available Rooms</div>
            <div className="font-semibold text-gray-900" data-testid="text-detail-available">
              {market.availableUnits?.toLocaleString()}
            </div>
          </div>
          <div className="border border-gray-100 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              Avg Weekly (Shared)
            </div>
            <div className="font-semibold text-gray-900" data-testid="text-detail-price-shared">
              ${market.avgPriceShared}/wk
            </div>
          </div>
          <div className="border border-gray-100 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              Avg Weekly (Private)
            </div>
            <div className="font-semibold text-gray-900" data-testid="text-detail-price-private">
              ${market.avgPricePrivate}/wk
            </div>
          </div>
          <div className="border border-gray-100 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Days to First Booking
            </div>
            <div className="font-semibold text-gray-900" data-testid="text-detail-days-first">
              {market.daysToFirstBooking} days
            </div>
          </div>
          <div className="border border-gray-100 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Days to 80% Occupancy
            </div>
            <div className="font-semibold text-gray-900" data-testid="text-detail-days-80">
              {market.daysTo80Occupancy} days
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/40 dark:to-indigo-950/40 border border-blue-200 dark:border-blue-900/50 rounded-xl p-5 mb-6" data-tour="research-city-stats">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Target className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0" />
                <h2 className="font-semibold text-gray-900 dark:text-foreground">Find Buy Box Properties in {market.city}</h2>
              </div>
              <p className="text-sm text-gray-600 dark:text-muted-foreground">
                Run an automated Zillow scan for {market.city}, {market.state} and underwrite every listing against your Buy Box criteria. Results appear in a report you can review and export.
              </p>
            </div>
            <button
              onClick={() => scanMutation.mutate()}
              disabled={scanMutation.isPending}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors sm:whitespace-nowrap sm:shrink-0 w-full sm:w-auto cursor-pointer"
              data-testid="button-run-buy-box-scan"
            >
              {scanMutation.isPending ? (
                <><Loader2 className="h-4 w-4 animate-spin" /> Starting Scan…</>
              ) : (
                <><Target className="h-4 w-4" /> Find Buy Box Properties</>
              )}
            </button>
          </div>
          {scanSetupErrors && (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 dark:border-amber-800/50 dark:bg-amber-950/30 px-4 py-3 space-y-1.5" data-testid="alert-scan-setup-errors">
              <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Complete your setup before running a scan:</p>
              {scanSetupErrors.buyBox && (
                <div className="text-sm text-amber-700 dark:text-amber-400 space-y-1.5" data-testid="alert-buybox-incomplete">
                  {scanSetupErrors.missingBuyBox.length > 0 ? (
                    <>
                      <p>
                        Fill in {scanSetupErrors.missingBuyBox.length === 1 ? "this Buy Box field" : `these ${scanSetupErrors.missingBuyBox.length} Buy Box fields`} before scanning:
                      </p>
                      <div className="flex flex-wrap gap-1.5" data-testid="list-scan-missing-buybox">
                        {scanSetupErrors.missingBuyBox.map((f) => (
                          <span
                            key={f.key}
                            className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/40 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-300"
                            data-testid={`chip-scan-missing-${f.key}`}
                          >
                            {f.label}
                          </span>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p>Please complete your Buy Box criteria before attempting to run this.</p>
                  )}
                  <Link href="/app/buy-box/settings">
                    <span className="underline font-medium cursor-pointer hover:text-amber-900 dark:hover:text-amber-200">Go to Buy Box settings →</span>
                  </Link>
                </div>
              )}
              {scanSetupErrors.underwriting && (
                <div className="text-sm text-amber-700 dark:text-amber-400 space-y-1.5" data-testid="alert-underwriting-incomplete">
                  {scanSetupErrors.missingUnderwriting.length > 0 ? (
                    <>
                      <p>
                        Fill in {scanSetupErrors.missingUnderwriting.length === 1 ? "this underwriting field" : `these ${scanSetupErrors.missingUnderwriting.length} underwriting fields`} before scanning:
                      </p>
                      <div className="flex flex-wrap gap-1.5" data-testid="list-scan-missing-underwriting">
                        {scanSetupErrors.missingUnderwriting.map((f) => (
                          <span
                            key={f.key}
                            className="inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/40 px-2.5 py-0.5 text-xs font-medium text-amber-800 dark:text-amber-300"
                            data-testid={`chip-scan-missing-${f.key}`}
                          >
                            {f.label}
                          </span>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p>Complete your underwriting defaults before attempting to run this.</p>
                  )}
                  <Link href="/app/underwriting/defaults">
                    <span className="underline font-medium cursor-pointer hover:text-amber-900 dark:hover:text-amber-200">Go to Underwriting defaults →</span>
                  </Link>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Task #1139 — AI Market Analysis. Reads the cached pre-computed
            row by default (free for all tiers). Growth+ users can also
            submit a custom prompt that runs Gemini live (not cached). */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-950/40 dark:to-purple-950/40 border border-indigo-200 dark:border-indigo-900/50 rounded-xl p-5 mb-6">
          <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              <h2 className="font-semibold text-gray-900 dark:text-foreground text-sm">AI Market Analysis</h2>
            </div>
            {aiSummary && aiSummaryGeneratedAt && (
              <p
                className="text-xs text-gray-500 dark:text-muted-foreground"
                data-testid="text-ai-generated-at"
                data-tour="research-city-ai"
              >
                {aiSummaryIsCustom ? "Custom analysis generated" : "Generated"} on{" "}
                {new Date(aiSummaryGeneratedAt).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            )}
          </div>

          {aiSummaryLoading ? (
            <div className="space-y-2 animate-pulse" data-testid="status-ai-loading">
              <div className="h-3 bg-indigo-200/50 rounded w-full" />
              <div className="h-3 bg-indigo-200/50 rounded w-11/12" />
              <div className="h-3 bg-indigo-200/50 rounded w-10/12" />
              <div className="h-3 bg-indigo-200/50 rounded w-full" />
              <div className="h-3 bg-indigo-200/50 rounded w-9/12" />
            </div>
          ) : isStreaming ? (
            <div data-testid="status-ai-streaming">
              <div className="flex items-center gap-2 text-indigo-700 dark:text-indigo-300 mb-2">
                <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                <span className="text-xs font-medium">{aiSummaryGeneratingLabel}</span>
              </div>
              <div
                className="text-sm text-gray-700 dark:text-foreground leading-relaxed space-y-2"
                data-testid="text-ai-summary-streaming"
              >
                {streamingText.split("\n\n").filter(Boolean).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
                <span className="inline-block w-2 h-4 align-text-bottom bg-indigo-500/70 animate-pulse" />
              </div>
            </div>
          ) : aiSummaryGenerating ? (
            <MarketAnalysisProgress
              label={aiSummaryGeneratingLabel}
              testId="status-ai-generating"
            />
          ) : generateIsForCurrentCity && generateMutation.isError && !aiSummary ? (
            <div className="flex flex-col gap-2" data-testid="status-ai-generate-error">
              <p className="text-xs text-red-600 dark:text-red-400">
                Couldn't generate the analysis. Please try again.
              </p>
              <button
                type="button"
                onClick={() => {
                  if (market) generateMutation.mutate({ city: market.city, state: market.state });
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 transition-all cursor-pointer self-start"
                data-testid="button-retry-ai-analysis"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Try again
              </button>
            </div>
          ) : aiSummary ? (
            <div data-testid="text-ai-summary">
              <div className="text-sm text-gray-700 dark:text-foreground leading-relaxed space-y-2">
                {aiSummary.split("\n\n").filter(Boolean).map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>
              {aiSummaryIsCustom && (
                <button
                  type="button"
                  onClick={() => setCustomSummary(null)}
                  className="text-xs text-indigo-600 dark:text-indigo-300 hover:underline mt-3"
                  data-testid="button-show-standard-analysis"
                >
                  ← Show standard analysis
                </button>
              )}
              <p className="text-[10px] text-gray-400 dark:text-muted-foreground italic mt-4 border-t border-gray-200/60 dark:border-border/60 pt-2" data-testid="text-ai-disclaimer">
                * This analysis was generated by AI and may contain inaccuracies. It is intended for informational purposes only and should not be relied upon as financial, legal, or investment advice. Always verify key facts independently before making decisions.
              </p>
            </div>
          ) : null}

          {canCustomizeAnalysis ? (
            <div className="mt-5 pt-4 border-t border-indigo-200/60 dark:border-indigo-900/50">
              <label
                htmlFor="ai-custom-prompt"
                className="block text-xs font-semibold text-gray-700 dark:text-foreground mb-2"
              >
                Customize this analysis
              </label>
              <p className="text-xs text-gray-500 dark:text-muted-foreground mb-2">
                Add specific questions or focus areas — for example, "focus on short-term rental regulations" or "compare with neighboring metros."
              </p>
              <textarea
                id="ai-custom-prompt"
                value={extraInstructions}
                onChange={(e) => setExtraInstructions(e.target.value)}
                maxLength={2000}
                rows={3}
                className="w-full rounded-md border border-indigo-200 dark:border-indigo-900/60 bg-white dark:bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="What would you like the analysis to focus on?"
                disabled={customPromptMutation.isPending}
                data-testid="textarea-ai-extra-instructions"
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-[10px] text-gray-400 dark:text-muted-foreground">
                  {extraInstructions.length}/2000 — runs live (not cached)
                </span>
                <button
                  type="button"
                  onClick={submitCustomPrompt}
                  disabled={customPromptMutation.isPending || extraInstructions.trim().length === 0}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer"
                  data-testid="button-run-custom-analysis"
                >
                  {customPromptMutation.isPending ? (
                    <>
                      <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                      Generating…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5" />
                      Run custom analysis
                    </>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <div
              className="mt-5 pt-4 border-t border-indigo-200/60 dark:border-indigo-900/50 flex items-start gap-2 text-xs text-gray-500 dark:text-muted-foreground"
              data-testid="text-ai-customize-locked"
            >
              <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                Want to customize this analysis with your own questions? Upgrade to Growth or Professional to run live custom prompts.
              </span>
            </div>
          )}
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6 flex items-start gap-2">
          <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-700" data-testid="text-data-info">
            {hasHistoricalData
              ? `These charts show real PadSplit marketplace data for the available months: ${xAxisTicks.map(offset => offsetToMonthLabel(offset)).join(", ")}. Time ranges end at the newest available data month; months without data are not filled in. On smaller screens, scroll the chart horizontally to see every month.`
              : "This city only has its latest snapshot available. Historical data points were not collected for this market."}
          </p>
        </div>

        <div className="bg-white border border-gray-100 rounded-xl p-5 mb-6" data-tour="research-city-stats">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-900">
              {showComparison ? "City Comparison" : `${market.city} — Historical Trend`}
            </h2>
            <div className="flex items-center gap-2">
              {!showComparison && (
                <button
                  onClick={() => setShowTrendsOverlay((v) => !v)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-all cursor-pointer ${
                    showTrendsOverlay
                      ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700"
                      : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                  data-testid="button-toggle-google-trends"
                >
                  <Activity className="h-3.5 w-3.5" />
                  {trendsLoading ? "Loading..." : "Google Trends"}
                </button>
              )}
              <button
                onClick={() => setShowComparison(!showComparison)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all cursor-pointer ${
                  showComparison
                    ? "bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200"
                    : "bg-blue-600 text-white hover:bg-blue-700 shadow-sm"
                }`}
                data-testid="button-toggle-comparison"
              >
                {showComparison ? "Show Single City" : "Compare Cities"}
              </button>
            </div>
          </div>
          {showTrendsOverlay && !showComparison && trendsError && (
            <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3" data-testid="text-trends-error">
              Google Trends data unavailable for this region. The chart is showing PadSplit data only.
            </div>
          )}
          {showTrendsOverlay && !showComparison && trendsData?.stale && trendsData?.fetchedAt && (
            <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 mb-3 flex items-center gap-1.5" data-testid="text-trends-stale-note">
              <span className="inline-block w-2 h-2 rounded-full bg-amber-400 shrink-0" />
              Google Trends data is from the last successful fetch ({new Date(trendsData.fetchedAt).toLocaleDateString()}) — live data temporarily unavailable.
            </div>
          )}
          {showTrendsOverlay && !showComparison && trendsData?.data?.length && (
            <div className="flex items-center gap-2 mb-3 text-[11px] text-gray-500" data-testid="text-trends-note">
              <span className="inline-block w-3 h-0.5 bg-emerald-500 rounded" />
              <span>Green line (right axis) shows Google Trends search interest for <strong>"rooms for rent"</strong> in the {market.city} metro area (0–100 index, relative to peak).</span>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 mb-4" role="group" aria-label="Chart time range" data-testid="chart-time-range-controls">
            <span className="text-xs font-medium text-gray-600 mr-1">Time range:</span>
            {(["all", 24, 12, 6] as const).map(range => (
              <button
                key={range}
                type="button"
                aria-pressed={chartRange === range}
                onClick={() => setChartRange(range)}
                data-testid={`button-chart-range-${range}`}
                className={`px-3 py-1.5 text-xs rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${
                  chartRange === range
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
              >
                {range === "all" ? "All" : `${range} months`}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-2 mb-2" data-testid="chart-metric-controls" data-tour="research-city-chart">
            {(Object.keys(chartMetricLabels) as ChartMetric[]).map((metric) => (
              <button
                key={metric}
                onClick={() => setChartMetric(metric)}
                className={`px-3 py-1.5 text-xs rounded-full border transition-all cursor-pointer ${
                  chartMetric === metric
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                }`}
                data-testid={`button-metric-${metric}`}
              >
                {chartMetricLabels[metric]}
              </button>
            ))}
          </div>

          <div className="flex items-start gap-1.5 mb-4 px-1">
            <HelpCircle className="h-3.5 w-3.5 text-gray-400 mt-0.5 shrink-0" />
            <p className="text-[11px] text-gray-500 leading-relaxed" data-testid="text-metric-description">
              <span className="font-medium text-gray-600">{chartMetricLabels[chartMetric]}:</span>{" "}
              {chartMetricDescriptions[chartMetric]}
            </p>
          </div>

          {!showComparison ? (
            <div className="overflow-x-auto" data-testid="single-city-chart-scroll">
            <div style={{ minWidth: monthChartMinWidth(xAxisTicks) }}>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={singleCityChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="monthOffset"
                  type="number"
                  domain={xAxisDomain}
                  padding={{ left: MONTH_AXIS_EDGE_PADDING_PX, right: MONTH_AXIS_EDGE_PADDING_PX }}
                  ticks={xAxisTicks}
                  interval={0}
                  tick={<MonthYearTick />}
                  height={45}
                />
                <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                {showTrendsOverlay && trendsData?.data?.length ? (
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: "#10b981" }} domain={[0, 100]} unit="" width={38} />
                ) : null}
                <Tooltip
                  content={<CenteredTooltip />}
                  wrapperStyle={{ pointerEvents: "none" }}
                  isAnimationActive={false}
                />
                {showTrendsOverlay && trendsData?.data?.length ? (
                  <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "6px" }} />
                ) : null}
                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey={chartMetric}
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name={chartMetricLabels[chartMetric]}
                  connectNulls={true}
                />
                {showTrendsOverlay && trendsData?.data?.length ? (
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="googleTrends"
                    stroke="#10b981"
                    strokeWidth={2}
                    strokeDasharray="5 3"
                    dot={{ r: 3 }}
                    name='Google Trends: "rooms for rent"'
                    connectNulls={true}
                  />
                ) : null}
              </LineChart>
            </ResponsiveContainer>
            </div>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500">
                    Select up to 50 cities to compare (including {market.city}). {compareCities.length + 1}/50 selected.
                  </p>
                  {compareCities.length >= 49 && (
                    <span className="text-xs text-red-500 font-medium">Maximum 50 cities reached</span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="compare-min-rooms-filter">
                  <span className="text-xs text-gray-500 font-medium">Min rooms:</span>
                  {COMPARE_MIN_ROOMS_PRESETS.map((preset) => (
                    <button
                      key={preset}
                      onClick={() => setCompareMinRooms(preset)}
                      className={`px-2.5 py-1 text-[11px] rounded-full border transition-all cursor-pointer ${
                        compareMinRooms === preset
                          ? "bg-gray-800 text-white border-gray-800"
                          : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      }`}
                      data-testid={`button-compare-min-rooms-${preset}`}
                    >
                      {preset}+
                    </button>
                  ))}
                  <button
                    onClick={selectAllCompare}
                    disabled={compareCities.length >= 49}
                    className="ml-auto px-3 py-1 text-[11px] rounded-full bg-brand text-white font-semibold transition-all cursor-pointer hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="button-compare-select-all"
                  >
                    Select All
                  </button>
                  {compareCities.length > 0 && (
                    <button
                      onClick={clearAllCompare}
                      className="px-3 py-1 text-[11px] rounded-full bg-white text-gray-600 border border-gray-200 font-semibold transition-all cursor-pointer hover:border-gray-300 hover:bg-gray-50"
                      data-testid="button-compare-clear-all"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto" data-testid="city-comparison-selector">
                  {comparePickerCities.map((m) => {
                    const isCurrentCity = m.city === market.city;
                    const isSelected = compareCities.includes(m.city);
                    const isDisabled = !isSelected && !isCurrentCity && compareCities.length >= 49;
                    return (
                      <button
                        key={m.id}
                        onClick={() => toggleCompareCity(m.city)}
                        disabled={isCurrentCity || isDisabled}
                        className={`px-2 py-1 text-[11px] rounded border transition-all ${
                          isCurrentCity
                            ? "bg-blue-100 text-blue-700 border-blue-300 cursor-default font-medium"
                            : isSelected
                            ? "bg-blue-50 text-blue-600 border-blue-200 cursor-pointer"
                            : isDisabled
                            ? "bg-gray-50 text-gray-300 border-gray-100 cursor-not-allowed"
                            : "bg-white text-gray-600 border-gray-200 hover:border-gray-300 cursor-pointer"
                        }`}
                        data-testid={`button-compare-${m.city.toLowerCase().replace(/\s/g, "-")}`}
                      >
                        {m.city}, {m.state}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="overflow-x-auto" data-testid="comparison-chart-scroll">
              <div style={{ minWidth: monthChartMinWidth(xAxisTicks) }}>
                <ResponsiveContainer width="100%" height={comparisonChartHeight}>
                  <LineChart data={comparisonData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="monthOffset"
                      type="number"
                      domain={xAxisDomain}
                      padding={{ left: MONTH_AXIS_EDGE_PADDING_PX, right: MONTH_AXIS_EDGE_PADDING_PX }}
                      ticks={xAxisTicks}
                      interval={0}
                      tick={<MonthYearTick />}
                      height={45}
                    />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      content={<CenteredTooltip />}
                      wrapperStyle={{ pointerEvents: "none", overflow: "visible" }}
                      isAnimationActive={false}
                    />
                    <Legend
                      wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }}
                    />
                    {activeCitiesInChart.map((city, i) => (
                      <Line
                        key={city}
                        type="monotone"
                        dataKey={city}
                        stroke={CHART_COLORS[i % CHART_COLORS.length]}
                        strokeWidth={city === market.city ? 3 : 2}
                        dot={{ r: 3 }}
                        name={city}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              </div>
            </>
          )}
        </div>

        <div className="border-t border-gray-100 pt-4">
          <p className="text-xs text-gray-400 leading-relaxed" data-testid="text-research-disclaimer">
            * Market data sourced from PadSplit marketplace analytics. Charts are composed of real data collected at intervals over the last 12+ months. Search-to-room ratio on historical points is estimated from searches and occupancy. This information is for research purposes only and should not be relied upon as the sole basis for investment decisions.
          </p>
        </div>
      </div>
    </AppLayout>
  );
}
